import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { fmtInt } from "@/lib/format";
import { ArrowUpFromLine } from "lucide-react";

const TYPES = {
  sample: "Numune",
  demo: "Demo",
  gift: "Hediye",
  fire: "Fire",
  count_deficit: "Sayım Eksiği",
  manual_decrease: "Manuel Düzeltme",
};

export default function StockDecrease() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [productId, setProductId] = useState("");
  const [movementType, setMovementType] = useState("sample");
  const [quantity, setQuantity] = useState("");
  const [companyId, setCompanyId] = useState("none");
  const [manualRecipient, setManualRecipient] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/products", { params: { is_active: true } }).then((r) => setProducts(r.data));
    api.get("/companies").then((r) => setCompanies(r.data));
  }, []);

  const selected = products.find((p) => p.id === productId);

  const submit = async (e) => {
    e.preventDefault();
    if (!productId) return toast.error("Ürün seçin");
    if (!quantity || Number(quantity) <= 0) return toast.error("Geçerli miktar girin");
    if (selected && Number(selected.current_stock) < Number(quantity)) {
      return toast.error("Stok yetersiz");
    }
    setSaving(true);
    try {
      await api.post("/stock/decrease", {
        product_id: productId,
        movement_type: movementType,
        quantity: Number(quantity),
        company_id: companyId === "none" ? null : companyId,
        manual_recipient: manualRecipient,
        description: description,
      });
      toast.success("Stok düşme kaydedildi");
      navigate("/products");
    } catch (err) {
      toast.error(err.response?.data?.detail || "İşlem başarısız");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="max-w-3xl space-y-5">
      <div className="azd-card p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-lg bg-[#DC2626]/10 text-[#DC2626] flex items-center justify-center">
            <ArrowUpFromLine size={18} />
          </div>
          <div>
            <h2 className="text-lg font-extrabold text-[#08285A]">Stok Düş</h2>
            <p className="text-xs text-[#6B7280]">Satış dışı stok çıkışları için</p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-xs font-semibold text-[#111827] mb-1.5">
              Ürün <span className="text-[#DC2626]">*</span>
            </label>
            <Select value={productId} onValueChange={setProductId}>
              <SelectTrigger data-testid="decrease-product"><SelectValue placeholder="Ürün seçin" /></SelectTrigger>
              <SelectContent>
                {products.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.product_code} — {p.product_name} ({fmtInt(p.current_stock)} {p.unit})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-[#111827] mb-1.5">
              Çıkış Tipi <span className="text-[#DC2626]">*</span>
            </label>
            <Select value={movementType} onValueChange={setMovementType}>
              <SelectTrigger data-testid="decrease-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(TYPES).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-[#111827] mb-1.5">
              Miktar ({selected?.unit || "-"}) <span className="text-[#DC2626]">*</span>
            </label>
            <Input data-testid="decrease-quantity" type="number" min="0.01" step="0.01"
                   value={quantity} onChange={(e) => setQuantity(e.target.value)} required />
          </div>

          <div>
            <label className="block text-xs font-semibold text-[#111827] mb-1.5">Firma (opsiyonel)</label>
            <Select value={companyId} onValueChange={setCompanyId}>
              <SelectTrigger data-testid="decrease-company"><SelectValue placeholder="Seçin" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Yok —</SelectItem>
                {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-[#111827] mb-1.5">Kişi/Firma (manuel)</label>
            <Input data-testid="decrease-recipient" value={manualRecipient}
                   onChange={(e) => setManualRecipient(e.target.value)} />
          </div>

          <div className="md:col-span-2">
            <label className="block text-xs font-semibold text-[#111827] mb-1.5">Açıklama</label>
            <Textarea data-testid="decrease-description" value={description}
                      onChange={(e) => setDescription(e.target.value)} rows={2} />
          </div>
        </div>

        {selected && quantity && (
          <div className="mt-5 p-4 bg-[#F6F8FB] rounded-lg border border-[#E5E7EB]">
            <div className="text-xs text-[#6B7280] uppercase tracking-wider font-bold mb-1">
              Yeni Stok Tahmini
            </div>
            <div className={`text-2xl font-extrabold ${
              Number(selected.current_stock) - Number(quantity || 0) <= Number(selected.critical_stock_level)
                ? "text-[#F59E0B]" : "text-[#08285A]"
            }`}>
              {fmtInt(Number(selected.current_stock) - Number(quantity || 0))} {selected.unit}
            </div>
          </div>
        )}
      </div>

      <div className="flex gap-3 justify-end">
        <Button type="button" variant="outline" onClick={() => navigate("/products")}>Vazgeç</Button>
        <Button type="submit" disabled={saving} data-testid="save-decrease"
                className="bg-[#0B3676] hover:bg-[#08285A] text-white">
          {saving ? "Kaydediliyor..." : "Stok Düşmeyi Kaydet"}
        </Button>
      </div>
    </form>
  );
}
